/**
 * PipelineColumnSkeleton - Skeleton loading para coluna do pipeline
 * 
 * Exibe um placeholder animado para uma coluna (etapa) do Kanban.
 * Mantém o mesmo layout visual da coluna real para transição suave.
 */

interface PipelineColumnSkeletonProps {
    /** Número de cards skeleton a renderizar na coluna */
    cardCount?: number;
    /** Se deve mostrar animação de pulse */
    animate?: boolean;
}

export const PipelineColumnSkeleton = ({
    cardCount = 3,
    animate = true
}: PipelineColumnSkeletonProps) => {
    const pulseClass = animate ? 'animate-pulse' : '';

    return (
        <div className="pipeline-column-skeleton flex flex-shrink-0 flex-col rounded-2xl border border-border bg-card h-full">
            {/* Header skeleton */}
            <div className={`p-4 border-b border-border ${pulseClass}`}>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <div className="h-5 bg-border rounded w-32" />
                        <div className="h-5 w-8 bg-secondary rounded-full" />
                    </div>
                    <div className="w-6 h-6 bg-secondary rounded" />
                </div>
            </div>

            {/* Cards skeleton */}
            {/* Os cards do esqueleto imitam o card compacto (cabeçalho de 2 linhas, linha de data e rodapé),
                para a troca carregando → conteúdo não "encolher" os cards na frente da pessoa. */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {Array.from({ length: cardCount }).map((_, index) => (
                    <div
                        key={`card-skeleton-${index}`}
                        className={`bg-card border border-border rounded-xl p-3 shadow-sm ${pulseClass}`}
                    >
                        {/* Avatar, nome e telefone */}
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-border rounded-full flex-shrink-0" />
                            <div className="flex-1 space-y-1.5">
                                <div className="h-3.5 bg-border rounded w-3/4" />
                                <div className="h-3 bg-secondary rounded w-1/2" />
                            </div>
                        </div>

                        {/* Data do contato e tempo na etapa */}
                        <div className="flex gap-1.5 mt-2">
                            <div className="h-4 bg-secondary rounded w-12" />
                            <div className="h-4 bg-secondary rounded w-24" />
                        </div>

                        {/* Rodapé com ações */}
                        <div className="flex items-center mt-2 pt-2 border-t border-border">
                            <div className="flex gap-1.5">
                                <div className="w-7 h-7 bg-secondary rounded-md" />
                                <div className="w-7 h-7 bg-secondary rounded-md" />
                                <div className="w-7 h-7 bg-secondary rounded-md" />
                                <div className="w-7 h-7 bg-secondary rounded-md" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Footer skeleton */}
            <div className={`p-3 border-t border-border ${pulseClass}`}>
                <div className="h-9 bg-secondary rounded-lg w-full" />
            </div>
        </div>
    );
};

/**
 * PipelineBoardSkeleton - Skeleton completo do board Kanban
 * 
 * Renderiza múltiplas colunas skeleton para simular o board completo.
 */
interface PipelineBoardSkeletonProps {
    /** Número de colunas skeleton a renderizar */
    columnCount?: number;
    /** Se deve mostrar header de admin */
    showAdminHeader?: boolean;
}

export const PipelineBoardSkeleton = ({
    columnCount = 4,
    showAdminHeader = false
}: PipelineBoardSkeletonProps) => {
    return (
        <div className="pipeline-page h-full flex flex-col pipeline-board-container">
            {/* Header skeleton */}
            <div className="flex justify-between items-center mb-4 animate-pulse">
                <div>
                    <div className="h-8 bg-border rounded w-32 mb-2" />
                    <div className="h-4 bg-secondary rounded w-64" />
                </div>

                {!showAdminHeader && (
                    <div className="flex gap-3">
                        <div className="h-10 bg-secondary rounded-xl w-36" />
                        <div className="h-10 bg-border rounded-xl w-32" />
                        <div className="h-10 bg-secondary rounded-xl w-32" />
                    </div>
                )}
            </div>

            {/* Board skeleton */}
            <div className="flex-1 overflow-hidden">
                <div className="flex h-full overflow-x-auto overflow-y-hidden pipeline-scroll-container">
                    {Array.from({ length: columnCount }).map((_, index) => (
                        <PipelineColumnSkeleton
                            key={`column-skeleton-${index}`}
                            cardCount={index % 2 === 0 ? 3 : 4} // Varia o número de cards
                            animate={true}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};
